import React, { useEffect, useState } from 'react';
import './index.css';
import './Mobile.css'; // Import the new mobile CSS
import Clock from './Clock';
import Mobile from './Mobile'; // Import the new Mobile component

const App = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [activeCard, setActiveCard] = useState(null);
  const [previousCard, setPreviousCard] = useState(null);
  const [productsClicked, setProductsClicked] = useState(false);
  const [contactClicked, setContactClicked] = useState(false);

  const vwToPx = (vw) => viewportWidth * (vw / 100);

  const handleScroll = (event) => {
    event.preventDefault();
    setScrollPosition((prevPosition) => {
      const scrollFactor = 0.2;
      const newPosition = prevPosition - event.deltaY * scrollFactor;
      const maxScroll = vwToPx(45) * 0;
      const minScroll = -vwToPx(45) * 3.3;

      if (newPosition > maxScroll) {
        return maxScroll;
      } else if (newPosition < minScroll) {
        return minScroll;
      } else {
        return newPosition;
      }
    });
  };

  const handleResize = () => {
    const newViewportWidth = window.innerWidth;
    setViewportWidth(newViewportWidth);
    setIsMobile(newViewportWidth <= 768);

    const maxScroll = vwToPx(45) * 0;
    const minScroll = -vwToPx(45) * 3.3;

    if (scrollPosition > maxScroll) {
      setScrollPosition(maxScroll);
    } else if (scrollPosition < minScroll) {
      setScrollPosition(minScroll);
    }
  };

  const scrollToFirstSquare = () => {
    const targetPosition = productsClicked ? vwToPx(45) * 0 : -vwToPx(45) * 0.85;
    const startPosition = scrollPosition;
    const distance = targetPosition - startPosition;
    const duration = 1000; // Increased duration for a slower effect
    let startTime = null;

    const animateScroll = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsedTime = timestamp - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      const newPosition = startPosition + distance * progress;
      setScrollPosition(newPosition);

      if (elapsedTime < duration) {
        window.requestAnimationFrame(animateScroll);
      }
    };

    window.requestAnimationFrame(animateScroll);
    setPreviousCard(activeCard);
    setActiveCard(null);
    setProductsClicked(!productsClicked);
    setContactClicked(false);
  };

  const scrollToFourthSquare = () => {
    const targetPosition = contactClicked ? vwToPx(45) * 0 : -vwToPx(45) * 3.3;
    const startPosition = scrollPosition;
    const distance = targetPosition - startPosition;
    const duration = 1000; // Increased duration for a slower effect
    let startTime = null;

    const animateScroll = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsedTime = timestamp - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      const newPosition = startPosition + distance * progress;
      setScrollPosition(newPosition);

      if (elapsedTime < duration) {
        window.requestAnimationFrame(animateScroll);
      }
    };

    window.requestAnimationFrame(animateScroll);
    setPreviousCard(activeCard);
    setActiveCard(null);
    setContactClicked(!contactClicked);
    setProductsClicked(false);
  };

  const handleCardToggle = (card) => {
    setScrollPosition(vwToPx(45) * 0);
    setProductsClicked(false);
    setContactClicked(false);
    if (activeCard === card) {
      setPreviousCard(activeCard);
      setActiveCard(null);
    } else {
      setPreviousCard(activeCard);
      setActiveCard(card);
    }
  };

  useEffect(() => {
    const checkMobile = () => {
      const newViewportWidth = window.innerWidth;
      setViewportWidth(newViewportWidth);
      setIsMobile(newViewportWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    window.addEventListener('wheel', handleScroll, { passive: false });
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('wheel', handleScroll);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('resize', checkMobile);
    };
  }, [scrollPosition, viewportWidth]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        } else {
          entry.target.classList.remove('visible');
        }
      });
    }, { threshold: 0.1 });

    const texts = document.querySelectorAll('.algorithm-text, .algorithm-text_upper');
    texts.forEach(text => {
      observer.observe(text);
    });

    return () => {
      texts.forEach(text => {
        observer.unobserve(text);
      });
    };
  }, []);

  if (isMobile) {
    return <Mobile />;
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-start justify-start">
      <nav className="navbar text-white font-thin">
        <a href="#about" className="nav-link" onClick={() => handleCardToggle('about')}>&lt;/ ABOUT &gt;</a>
        <a href="#products" className="nav-link" onClick={scrollToFirstSquare}>&lt;/ PRODUCTS &gt;</a>
        <a href="#contact" className="nav-link" onClick={scrollToFourthSquare}>&lt;/ CONTACT &gt;</a>
      </nav>

      <h1 className="text-white leading-none unselectable">
        <span className="font-thin">OBERO</span>
        <span className="font-semibold">N≥X</span>
      </h1>
      <div className="new-text unselectable">
        <span className="font-regular word white-text">QUANT</span>
        <span className="font-extralight word">SOLUTIONS</span>
        <span className="font-extralight word">FOR</span>
        <span className="font-regular word white-text">COMMODITY</span>
        <span className="font-extralight word">MARKETS</span>
      </div>

      <div className="squares-container">
        <div className="square square-1" style={{ top: `${scrollPosition}px` }}>
          <a href="#algorithms" className="algorithm-link algorithm-text">
            <span className="yellow-symbol">&lt;/</span> ALGORITHMS <span className="yellow-symbol">&gt;</span>
          </a>
          <div className="code-block">
            <span className="keyword">def</span> <span className="function-name">calculate_fft</span>(<span className="variable">data</span>):
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;<span className="variable">fft_values</span> = <span class="variable">np</span>.<span class="function">fft</span>.<span class="function">fft</span>(<span class="variable">data</span>)
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;<span class="variable">fft_frequencies</span> = <span class="variable">np</span>.<span class="function">fft</span>.<span class="function">fftfreq</span>(<span class="variable">len</span>(<span class="variable">data</span>))
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;<span class="keyword">return</span> <span class="variable">fft_values</span>, <span class="variable">fft_frequencies</span>
            <br /><br />
            <span class="keyword">def</span> <span class="function-name">generate_signals</span>(<span class="variable">data</span>):
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;<span class="variable">fft_values</span>, <span class="variable">fft_frequencies</span> = <span class="function-name">calculate_fft</span>(<span class="variable">data</span>[<span class="string">'close'</span>])
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;<span class="variable">signals</span> = <span class="variable">pd</span>.<span class="function">DataFrame</span>(<span class="variable">index</span>=<span class="variable">data</span>.<span class="function">index</span>)
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;<span class="variable">signals</span>[<span class="string">'signal'</span>] = <span class="number">0.0</span>
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;<span class="variable">signals</span>[<span class="string">'fft_real'</span>] = <span class="variable">np</span>.<span class="function">real</span>(<span class="variable">fft_values</span>)
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;<span class="variable">signals</span>[<span class="string">'fft_imag'</span>] = <span class="variable">np</span>.<span class="function">imag</span>(<span class="variable">fft_values</span>)
            <br /><br />

            &nbsp;&nbsp;&nbsp;&nbsp;<span class="variable">signals</span>[<span class="string">'signal'</span>][<span class="variable">signals</span>[<span class="string">'fft_real'</span>] <span class="number">0</span>] = <span class="number">1.0</span>
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;<span class="variable">signals</span>[<span class="string">'positions'</span>] = <span class="variable">signals</span>[<span class="string">'signal'</span>].<span class="function">diff</span>()
            <br /><br />
            &nbsp;&nbsp;&nbsp;&nbsp;<span class="keyword">return</span> <span class="variable">signals</span>
          </div>
        </div>
        <div className="square square-2" style={{ top: `${scrollPosition + vwToPx(43)}px` }}>
          <a href="#algorithms" className="algorithm-link algorithm-text">
            <span className="yellow-symbol">&lt;/</span> INDICATORS <span className="yellow-symbol">&gt;</span>
          </a>
          <img src="./3d_plot_full.svg" alt="Fading Plot" className="svg-image" />
        </div>
        <div className="square square-3" style={{ top: `${scrollPosition + vwToPx(72)}px` }}>
          <a href="#algorithms" className="algorithm-link algorithm-text">
            <span className="yellow-symbol">&lt;/</span> REAL TIME DATA <span class="yellow-symbol">&gt;</span>
          </a>
          <div className="clock-container">
            <Clock city="LONDON" offset={1} customClass="london-clock unselectable" />
            <Clock city="NEW YORK" offset={-4} customClass="newyork-clock unselectable" />
            <Clock city="SH/HK" offset={8} customClass="shhk-clock unselectable" />
          </div>
        </div>
        <div className="square square-4 upper-left" style={{ top: `${scrollPosition + vwToPx(102)}px` }}>
          <a href="#contact" className="algorithm-link algorithm-text_upper">
            <span class="yellow-symbol">&lt;/</span> CONTACT <span class="yellow-symbol">&gt;</span>
          </a>
          <div className="contact-info">
            <div className="message unselectable">We will be thrilled to work with you. Shoot us a mail and we will get back to you</div>
            <a href="mailto:hello@oberon-x.com" className="email">hello@oberon-x.com</a>
          </div>
        </div>
      </div>

      {activeCard === 'about' && (
        <div className={`red-card slide-in-left about-card`}>
          <div className="card-text-vertical-right">ABOUT</div>
          <div className="about-text3">
            <div>We design State of the Art Solutions for commodities markets, from the trading desk to the back office. A multidisciplinary team with more than 15 years of experience in developing, testing, and deploying top of the line solutions for the most demanding industries.</div>
          </div>
          <div className="about-text1">
            <div><span className="about-algorithms">ALGORITHMS</span> AS A <span className="about-algorithms">SERVICE</span> IS WHAT WE DO</div>
          </div>
          <div className="about-text2">
            <div><span className="about-commodities">COMMODITIES</span> IS WHAT WE <span className="about-commodities ">THRIVE ON</span></div>
          </div>
        </div>
      )}

      {previousCard === 'about' && activeCard !== 'about' && (
        <div className={`red-card slide-out-left`}>
          <div className="card-text-vertical-right">ABOUT</div>
        </div>
      )}
    </div>
  );
}

export default App;
